import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { catchError, EMPTY, finalize, map, Subject, Subscription, switchMap } from 'rxjs';
import { PriceMatrix } from 'src/app/modules/pos/quotes/entities/price-matrix.class';
import { QuoteDetailsService } from 'src/app/modules/pos/quotes/services/quote-details.service';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { Customer } from 'src/app/modules/shared/entities/customer.class';
import { AppInfoService } from 'core/app-info/app-info.service';
import { SearchService } from 'core/search/search.service';
import { CountryCode } from 'core/app-info/country-specifics.enum';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PriceAsService } from '../../services/price-as-service/price-as.service';
import { ProductValidationService } from '../../services/product-validation-service/product-validation.service';
import { LastQuotesListComponent } from '../last-quotes-list/last-quotes-list.component';

@Component({
  selector: 'app-price-as',
  templateUrl: './price-as.component.html',
  styleUrls: ['./price-as.component.scss'],
  standalone: false
})
export class PriceAsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() initialPrice = null;
  @Input() productNumber = '';
  @Input() catalogCode = '';
  @ViewChild('accountManagerSelect') accountManagerInput: ElementRef<HTMLInputElement>;
  @ViewChild('customerSelect') customerInput: ElementRef<HTMLInputElement>;
  @ViewChild('priceMatrixInput') priceMatrixInput: ElementRef<HTMLInputElement>;

  priceAsFormGroup = new UntypedFormGroup({
    accountManager: new UntypedFormControl({ value: null, disabled: true }),
    customer: new UntypedFormControl({ value: null, disabled: true }),
    priceMatrix: new UntypedFormControl({ value: null, disabled: true }),
    quantity: new UntypedFormControl(1, { updateOn: 'blur' }),
  });

  isMobile = false;
  isLoading = false;
  accountManagerList: AccountManager[];
  filteredAccountManagerList: AccountManager[];
  customerList: Customer[];
  filteredCustomerList: Customer[];
  filteredPriceMatrixList: PriceMatrix[];
  priceMatrixList: PriceMatrix[];
  appStrings: AppLocalization;
  selectedAccountManager: AccountManager = null;
  selectedCustomer: Customer = null;
  selectedPriceMatrix: PriceMatrix = null;
  currencySymbol = '';
  templateValues = {};
  listPrice = null;
  netPrice = null;
  hasNetPrice = false;
  leadStatusCodeList: any;
  lnStatusCodeList: any;
  amValidationMessage = '';
  customerValidationMessage = '';
  priceMatrixValidationMessage = '';
  countryCode = '';
  isLead = false;
  leadPriceMatrix: PriceMatrix;
  desktopAddToQuoteStyling = {
    position: { top: '12vh' },
    width: '75vw',
    maxHeight: '90vh'
  };
  private limitedMarketingCheck$ = new Subject<Customer>();
  private subs = new Subscription();
  mobileAddToQuoteStyling = {
    position: { top: 'unset' },
    minHeight: '100dvh',
    minWidth: '100vw'
  };

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private searchService: SearchService,
    private appInfoService: AppInfoService,
    private priceAsService: PriceAsService,
    private quoteDetailsService: QuoteDetailsService,
    private deviceService: DeviceDetectorService,
    private productValidationService: ProductValidationService,
  ) { }

  ngOnInit(): void {
    this.countryCode = this.appInfoService.userData.UserInfo.CountryCode;
    this.appStrings = this.appInfoService.appStrings;
    this.currencySymbol = this.appInfoService.currencySymbol;
    this.isMobile = this.deviceService.isMobile();
    this.subscribeToLimitedMarketingValidation();
    this.initData();

    this.priceAsFormGroup.controls.quantity.valueChanges.subscribe(newValue => {
      if (newValue < 1) {
        this.priceAsFormGroup.controls.quantity.setValue(1);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updatePrices();
    if (changes.productNumber?.firstChange === false && this.selectedCustomer) {
      this.limitedMarketingCheck$.next(this.selectedCustomer);
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private subscribeToLimitedMarketingValidation() {
    this.subs.add(this.limitedMarketingCheck$.pipe(
      switchMap(customer => {
        const productNumber = this.productNumber;
        this.isLoading = true;
        return this.productValidationService.getLimitedMarketingNotifications(productNumber, customer.id, !!customer.isLead).pipe(
          catchError(() => EMPTY),
          finalize(() => this.isLoading = false),
          map(notifications => ({ productNumber, notifications }))
        );
      })
    ).subscribe(({ productNumber, notifications }) => {
      if (notifications.length) {
        this.productValidationService.openLimitedMarketingDialog(productNumber, notifications, this.catalogCode);
      }
    }));
  }

  /**
   * Updates prices for the new product
   */
  updatePrices() {
    if (this.priceAsFormGroup.controls.priceMatrix.value) {
      this.getPricesForCustomer();
    } else {
      // If price matrix is not selected, initial price is used
      this.listPrice = this.initialPrice;
    }
  }

  private initData() {
    this.listPrice = this.initialPrice;
    this.quoteDetailsService.getStaticValues((staticValues: any[]) => {
      if (this.appInfoService.isAccountManager()) {
        this.initAccountManager();
      } else {
        this.priceAsFormGroup.controls.accountManager.enable();
        this.accountManagerList = staticValues[1];
        this.filteredAccountManagerList = staticValues[1];
      }
      this.leadStatusCodeList = staticValues[0].LeadStatus;
      this.lnStatusCodeList = staticValues[0].LnStatus;
      this.priceMatrixList = staticValues[3];

      // Price Matrix should be enabled for Admins
      if (this.appInfoService.isAdminTypeUser()) {
        this.filterPriceMatrixList();
        this.priceAsFormGroup.controls.priceMatrix.enable();
      }

      // Persist price-as input selection on mobile devices
      if (this.isMobile) {
        if (!this.appInfoService.isAccountManager() && this.priceAsService.quoteInfo?.accountManager) {
          this.priceAsFormGroup.controls.accountManager.setValue(this.priceAsService.quoteInfo?.accountManager);
          this.selectedAccountManager = this.priceAsService.quoteInfo?.accountManager;
          this.loadCustomersAndLeadsList();
        }

        this.priceAsFormGroup.controls.customer.setValue(this.priceAsService.quoteInfo?.customer);
        this.selectedCustomer = this.priceAsService.quoteInfo?.customer;
        this.isLead = this.priceAsService.quoteInfo?.customer?.isLead;
        if (this.selectedCustomer) {
          this.limitedMarketingCheck$.next(this.selectedCustomer);
        }

        if (this.priceAsService.quoteInfo?.priceMatrix) {
          this.priceAsFormGroup.controls.priceMatrix.setValue(this.priceAsService.quoteInfo?.priceMatrix);
          this.selectedPriceMatrix = this.priceAsService.quoteInfo?.priceMatrix;
          this.updatePrices();
        }
      }
    });
  }

  /**
   * Initializes the Account Manager data based on user type
   */
  private initAccountManager() {
    this.priceAsService.getAccountManagerDetails(this.appInfoService.userData.UserInfo.EmployeeId).subscribe(accountManager => {
      this.selectedAccountManager = accountManager;
      this.priceAsFormGroup.controls.accountManager.setValue(accountManager);
      this.priceAsFormGroup.controls.accountManager.disable();
      this.priceAsFormGroup.controls.priceMatrix.enable();
      this.filterPriceMatrixList();

      if (accountManager) {
        this.loadCustomersAndLeadsList();
      }
    });
  }

  /**
   * Retrieves a list of Customers and Leads based on selected Account Manager
   */
  private loadCustomersAndLeadsList() {
    this.priceAsService.getCustomersAndLeads(this.selectedAccountManager.employeeId).subscribe(customers => {
      this.customerList = customers;
      this.filteredCustomerList = customers;
      this.priceAsFormGroup.controls.customer.enable();
    });
  }

  /**
   * Filter Account Manager List
   * @param searchValue - The search value
   */
  filterAccountManagerList(searchValue?: string) {
    if (!searchValue) {
      this.filteredAccountManagerList = this.accountManagerList;
      this.selectedAccountManager = null;
      if (!this.appInfoService.isAdminTypeUser) {
        this.priceAsFormGroup.controls.priceMatrix.disable();
      }
      this.resetCustomer();
      this.resetPriceMatrix();
      return;
    }

    this.filteredAccountManagerList = this.searchService.filterResults(
      searchValue,
      this.accountManagerList,
      ['name'],
      ['name']
    );
  }

  checkIfSelectedAccountManager(event) {
    if (this.isMobile && !event.target.value.length) {
      this.selectedAccountManager = null;
      this.setQuoteInfo();
    }
  }

  private resetCustomer() {
    this.priceAsFormGroup.controls.customer.setValue(null);
    this.priceAsFormGroup.controls.customer.disable();
    this.selectedCustomer = null;
  }

  private resetPriceMatrix() {
    this.priceAsFormGroup.controls.priceMatrix.setValue(null);
    this.selectedPriceMatrix = null;
    this.listPrice = this.initialPrice;
    this.netPrice = null;
    this.hasNetPrice = false;
  }

  /**
   * Display function for Account Manager
   * @param accountManager - The account manager object
   */
  displayAccountManager(accountManager: AccountManager) {
    return accountManager ? accountManager.name : '';
  }

  /**
   * Account Manager selection changed
   * @param event - The event
   */
  onAccountManagerSelect(event$: MatAutocompleteSelectedEvent) {
    const newAccountManager: AccountManager = event$.option.value;
    this.priceAsFormGroup.controls.priceMatrix.enable();
    this.filterPriceMatrixList();

    if (!newAccountManager) {
      this.priceAsFormGroup.controls.accountManager.setValue(this.selectedAccountManager);
      this.filteredAccountManagerList = this.accountManagerList;
      return;
    }

    this.amValidationMessage = '';
    this.selectedAccountManager = newAccountManager;
    this.resetCustomer();
    this.resetPriceMatrix();
    this.loadCustomersAndLeadsList();
    this.setQuoteInfo();
    this.accountManagerInput?.nativeElement.blur();
  }

  /**
   * Filter Customer List
   * @param searchValue - The search value
   */
  filterCustomerList(searchValue?: string) {
    if (!searchValue) {
      this.filteredCustomerList = this.customerList;
      return;
    }

    this.filteredCustomerList = this.searchService.filterResults(
      searchValue,
      this.customerList,
      ['name', 'bpNumber', 'childName1'],
      ['name']
    );
  }

  /**
   * Display function for Customer
   * @param customer - The Customer object
   */
  displayCustomer(customer: Customer) {
    return customer ? customer.name : '';
  }

  /**
   * Customer selection changed
   * @param event - The event
   */
  onCustomerSelect(event$: MatAutocompleteSelectedEvent) {
    const newCustomer: Customer = event$.option.value;
    if (!newCustomer) {
      this.priceAsFormGroup.controls.customer.setValue(this.selectedCustomer);
      this.filteredCustomerList = this.customerList;
      return;
    }
    this.isLead = newCustomer.isLead;
    this.selectedCustomer = newCustomer;
    this.customerValidationMessage = '';
    this.limitedMarketingCheck$.next(newCustomer);
    const priceMatrixCall =
      newCustomer.isLead ? this.priceAsService.getLeadDetails(newCustomer.id) :
        this.priceAsService.getCustomerPriceMatrix(newCustomer.id);

    priceMatrixCall.subscribe(response => {
      const priceMatrix = newCustomer.isLead ? response.priceMatrix : response;
      this.selectedPriceMatrix = priceMatrix;
      this.priceAsFormGroup.controls.priceMatrix.setValue(priceMatrix);
      this.priceMatrixValidationMessage = '';
      this.leadPriceMatrix = response.priceMatrix;
      this.filterPriceMatrixList();
      this.getPricesForCustomer();
      this.currencySymbol = response.currencySign;
      this.setQuoteInfo();
    });

    this.customerInput?.nativeElement.blur();
  }

  /**
   * Search for a given price matrix value into price matrix list, and add to the list if not found in it
   * @param priceMatrix - Searched price matrix
   */
  private addListPriceInPriceMatrixList(priceMatrix: PriceMatrix) {
    if (priceMatrix) {
      const publicListPriceMatrix = this.priceMatrixList.find(
        priceMatrixItem => priceMatrixItem.priceList === priceMatrix.priceList
      );
      if (!publicListPriceMatrix) {
        this.priceMatrixList.push(priceMatrix);
      }
    }
  }

  /**
   * Display function for Price Matrix
   * @param priceMatrix - The PriceMatrix object
   */
  displayPriceMatrix(priceMatrix: PriceMatrix) {
    if (priceMatrix) {
      return priceMatrix.priceList ? priceMatrix.priceList : '';
    }
  }

  /**
   * Filter Price Matrix List
   * @param searchValue - The search value
   */
  filterPriceMatrixList(searchValue?: string) {
    let initialList: PriceMatrix[];
    if (this.isLead) {
      this.addListPriceInPriceMatrixList(this.leadPriceMatrix);
    }

    if (this.countryCode === CountryCode.US && !this.isLead) {
      initialList = this.priceMatrixList.filter(
        priceMatrixItem =>
          priceMatrixItem.priceList !== this.appStrings.priceMatrix_industrial &&
          priceMatrixItem.priceList !== this.appStrings.priceMatrix_public
      );
    } else {
      if (this.isLead && this.countryCode === CountryCode.US) {
        initialList = this.priceMatrixList.filter(
          priceMatrixItem =>
            priceMatrixItem.priceList !== this.appStrings.priceMatrix_public
        );
      } else {
        initialList = this.priceMatrixList.filter(
          priceMatrixItem =>
            priceMatrixItem.priceList !== this.appStrings.priceMatrix_industrial ||
            (this.isLead !== true && priceMatrixItem.priceList !== this.appStrings.priceMatrix_public)
        );
      }
    }

    if (!searchValue) {
      this.filteredPriceMatrixList = initialList;
      return;
    }
    this.filteredPriceMatrixList = this.searchService.filterResults(
      searchValue,
      initialList,
      ['priceList']
    );
  }

  /**
   * Price Matrix selection changed
   * @param event - The event
   */
  onPriceMatrixSelect(event$: MatAutocompleteSelectedEvent) {
    const newPriceMatrix: PriceMatrix = event$.option.value;
    if (!newPriceMatrix) {
      this.priceAsFormGroup.controls.priceMatrix.setValue(this.selectedPriceMatrix);
      this.filteredPriceMatrixList = this.priceMatrixList;
      return;
    }

    this.selectedPriceMatrix = newPriceMatrix;
    this.priceMatrixValidationMessage = '';
    this.getPricesForCustomer();
    this.setQuoteInfo();
    this.priceMatrixInput?.nativeElement.blur();
  }

  private getPricesForCustomer() {
    if (!this.selectedPriceMatrix) {
      return;
    }

    this.priceAsService.getProductPrice({
      customerId: this.selectedCustomer?.id,
      bpNumber: this.selectedCustomer?.bpNumber,
      quantity: this.priceAsFormGroup.controls.quantity.value,
      priceMatrix: this.selectedPriceMatrix.priceList,
      productNumber: this.productNumber,
    }).subscribe(prices => {
      this.listPrice = prices.listPrice;
      this.netPrice = prices.netPrice;
      this.hasNetPrice = typeof prices.netPrice === 'number';
    });
  }

  addToQuote() {
    if (!this.selectedAccountManager) {
      this.amValidationMessage = `${this.appStrings.products_amErrorBase} ${this.appStrings.button_addToQuote?.toUpperCase()}`;
      return;
    }

    const styling = this.isMobile ? this.mobileAddToQuoteStyling : this.desktopAddToQuoteStyling;
    this.dialog.open(LastQuotesListComponent, {
      data: {
        formValues: this.priceAsFormGroup.value,
        productNumber: this.productNumber,
        accountManager: this.selectedAccountManager,
        customer: this.selectedCustomer
      },
      ...styling
    });
  }

  startNewQuote() {
    if (!this.selectedAccountManager) {
      this.amValidationMessage = `${this.appStrings.products_amErrorBase} ${this.appStrings.products_startNewQuote?.toUpperCase()}`;
      return;
    }
    this.setQuoteInfo();
    this.router.navigate(['/quotes/new/priceAs']);
  }

  private setQuoteInfo() {
    this.priceAsService.quoteInfo = {
      accountManager: this.selectedAccountManager,
      customer: this.selectedCustomer,
      priceMatrix: this.selectedPriceMatrix,
      product: {
        productNumber: this.productNumber,
        quantity: this.priceAsFormGroup.controls.quantity.value
      }
    };
  }
}
